import { MODEL_TOTAL } from './modelInventory.generated';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useAuthStore } from '@/store';
import { accountApi } from '@/lib/api';
import { useEarnProgramsRefreshStore } from '@/store/earnProgramsRefresh';
import { trackEvent } from '@/constants/utils/analytics';

// Action handlers for checklist items. Items with `action: '<slug>'` route
// through here instead of using `route`. Mirrors the pattern in
// `lib/bannerActions.js` so the same surface (e.g. open_import) stays
// consistent across banners and the Get-Started checklist.
const CHECKLIST_ACTIONS = {
  open_import: ({ openModal, isXS }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android' || isXS) {
      openModal('SettingsModalMobile', { initialScreen: 'import' });
    } else {
      openModal('SettingsModalWeb', { tab: 'import' });
    }
  },
};

export function runChecklistAction(slug, deps) {
  const handler = CHECKLIST_ACTIONS[slug];
  if (handler) handler(deps);
}

// Matomo name = item id or plus/free; value = 1 if Plus, 0 if free.
export function trackChecklistEvent(action, name) {
  const isPlus = !!useAuthStore.getState().user?.flags?.isPlus;
  trackEvent('checklist', action, name ?? (isPlus ? 'plus' : 'free'), isPlus ? 1 : 0);
}

// ──────────────────────────────────────────────────────────────
// GOAL: Drive new users through the highest-converting feature
// paths as fast as possible. Data shows converters use 2+ features
// (median) vs 1 for non-converters, and feature breadth is the
// single strongest predictor of conversion. Each item is ordered
// by the natural discovery sequence proven in production data
// (firstMessage → file upload → image → memory → voice → models
// → import).
//
// Plus users see credit rewards that overlap earn programs.
// Free users get the same Get Started tour with no credit chrome
// and no grants — discovery only.
// ──────────────────────────────────────────────────────────────

export const CHECKLIST_ITEMS = [
  {
    id: 'send_message',
    icon: 'comment',
    label: 'Send a message',
    hint: 'Ask anything',
    route: '/app',
    credits: 0,
    check: (u) => (u?.stats?.total_sent_message_all_count || 0) > 0,
  },
  {
    id: 'create_image',
    icon: 'image',
    label: 'Create an image',
    hint: '"make me an image of…"',
    route: '/app',
    credits: 10,
    check: (u) => (u?.stats?.total_sent_message_image_count || 0) > 0,
  },
  {
    id: 'upload_file',
    icon: 'file-arrow-up',
    label: 'Upload a file',
    hint: 'PDF, image, or code',
    route: '/files',
    credits: 10,
    check: (u) => (u?.tracking?.cachedFileCount || 0) > 0,
  },
  {
    id: 'add_memory',
    icon: 'brain',
    label: 'Add a memory',
    hint: 'Personalize your AI',
    route: '/memory',
    credits: 20,
    check: (u) => (u?.tracking?.cachedMemoryCount || 0) > 0,
  },
  {
    id: 'try_voice',
    icon: 'microphone',
    label: 'Try voice mode',
    hint: 'Talk hands-free',
    route: '/app',
    credits: 30,
    check: (u) => (u?.tracking?.cachedVoiceSessionCount || 0) > 0,
  },
  {
    id: 'try_models',
    icon: 'layer-group',
    label: 'Try 3 AI models',
    hint: `${MODEL_TOTAL} to choose from`,
    route: '/models',
    credits: 25,
    check: (u) => (u?.tracking?.cachedUniqueModelsUsedCount || 0) >= 3,
  },
  {
    // Mirrors the `pinned_conversation_first` earn program (R-may-42).
    // Pinning has 2.2× Plus conversion lift — surface it as a discovery hook.
    id: 'pin_chat',
    icon: 'thumbtack',
    label: 'Pin a chat',
    hint: 'Keep your favorites at the top',
    route: '/app',
    credits: 20,
    check: (u) => (u?.pinnedConversations?.length || 0) > 0,
  },
  {
    id: 'import_chats',
    icon: 'file-import',
    label: 'Import your chats',
    hint: 'Gab AI, ChatGPT, Claude, or Grok',
    action: 'open_import',
    credits: 200,
    check: (u) => (u?.tracking?.cachedImportedConversationCount || 0) > 0,
  },
  {
    id: 'verify_email',
    icon: 'envelope-circle-check',
    label: 'Verify your email',
    hint: 'Confirm your address',
    route: '/settings?tab=account',
    credits: 25,
    check: (u) => u?.flags?.isEmailVerified === true,
  },
  {
    id: 'complete_profile',
    icon: 'user-check',
    label: 'Complete your profile',
    hint: 'Add your name & role',
    route: '/settings?tab=account',
    credits: 25,
    check: (u) => !!(u?.fullName && u?.professionalRole),
  },
];

const DAILY_CREDITS = 10;
const CHECKLIST_MAX_ACCOUNT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function useChecklist() {
  const user = useAuthStore(state => state.user);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const fetchEarnPrograms = useEarnProgramsRefreshStore((s) => s.fetchEarnPrograms);

  const completionMap = useMemo(() => {
    const map = {};
    for (const item of CHECKLIST_ITEMS) {
      map[item.id] = item.check(user);
    }
    return map;
  }, [user]);

  const completedCount = useMemo(
    () => CHECKLIST_ITEMS.filter(i => completionMap[i.id]).length,
    [completionMap],
  );

  const earnableCredits = useMemo(() => {
    if (!user?.flags?.isPlus) return 0;
    return CHECKLIST_ITEMS.filter(i => !completionMap[i.id] && i.credits > 0)
      .reduce((sum, i) => sum + i.credits, 0);
  }, [completionMap, user?.flags?.isPlus]);

  const sortedItems = useMemo(() => {
    const incomplete = CHECKLIST_ITEMS.filter(i => !completionMap[i.id]);
    const completed = CHECKLIST_ITEMS.filter(i => completionMap[i.id]);
    return [...incomplete, ...completed];
  }, [completionMap]);

  const total = CHECKLIST_ITEMS.length;
  const isComplete = completedCount >= total;

  const hasUser = Boolean(user?.id);
  const isGhost = !!user?.flags?.isGhost;
  const isPlus = !!user?.flags?.isPlus;
  const showCredits = hasUser && isPlus;
  // Only pin these prompts during the first month. Missing or invalid signup
  // dates stay hidden; earning credits in Settings has no account-age cutoff.
  const createdAt = user?.createdAt ? new Date(user.createdAt).getTime() : NaN;
  const accountAge = Date.now() - createdAt;
  const canShowPinnedChecklist = hasUser && !isGhost
    && accountAge >= 0 && accountAge <= CHECKLIST_MAX_ACCOUNT_AGE_MS;
  const shouldShow = canShowPinnedChecklist && !isComplete;
  const shouldShowDaily = canShowPinnedChecklist && showCredits;

  // --- Daily claim state (Plus users only) ---
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyAvailable, setDailyAvailable] = useState(false);
  const [dailyClaiming, setDailyClaiming] = useState(false);
  const [dailyJustClaimed, setDailyJustClaimed] = useState(false);
  const dailyCheckedRef = useRef(false);

  useEffect(() => {
    if (!shouldShowDaily || dailyCheckedRef.current) return;
    dailyCheckedRef.current = true;
    setDailyLoading(true);

    fetchEarnPrograms()
      .then((programs) => {
        const daily = programs.find(p => p.key === 'daily_claim');
        setDailyAvailable(daily?.isAvailable ?? false);
      })
      .catch(() => {})
      .finally(() => setDailyLoading(false));
  }, [shouldShowDaily, fetchEarnPrograms]);

  const claimDaily = useCallback(async () => {
    if (dailyClaiming || !dailyAvailable) return false;
    try {
      setDailyClaiming(true);
      await accountApi.claimDailyCredits();
      setDailyAvailable(false);
      setDailyJustClaimed(true);
      fetchUserCredits?.();
      return true;
    } catch {
      return false;
    } finally {
      setDailyClaiming(false);
    }
  }, [dailyClaiming, dailyAvailable, fetchUserCredits]);

  return {
    completionMap,
    completedCount,
    earnableCredits,
    sortedItems,
    total,
    isComplete,
    shouldShow,
    shouldShowDaily,
    showCredits,
    isPlus,
    dailyLoading,
    dailyAvailable,
    dailyClaiming,
    dailyJustClaimed,
    dailyCredits: DAILY_CREDITS,
    claimDaily,
  };
}
